import { useCallback, useEffect, useRef, useState } from 'react';
import { useTX } from '@transifex/react';
import {
  createSseClient,
  type SseClient,
  type SseXpEvent,
  type GamificationStatsDto,
} from '~/lib/sse-client';
import { buildSseUrlWithToken } from '~/lib/sse-token';
import { MAX_EVENT_LOG, showXpEventToast } from '~/lib/sse-event-handler';
import { postXpAcknowledgements, XP_ACK_DEBOUNCE_MS } from '~/lib/sse-acknowledge';
import { SentryClient } from '~/lib/sentry.client';

export type EventLogEntry = {
  id: string;
  event: SseXpEvent;
};

export type SseConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export type UseSseNotificationsOptions = {
  autoConnect?: boolean;
};

export type UseSseNotificationsReturn = {
  status: SseConnectionStatus;
  eventLog: EventLogEntry[];
  stats: GamificationStatsDto | null;
  handleEvent: (event: SseXpEvent) => void;
  connect: () => void;
  disconnect: () => void;
};

export function useSseNotifications(
  options?: UseSseNotificationsOptions
): UseSseNotificationsReturn {
  const { autoConnect = true } = options ?? {};
  const tx = useTX();
  const locale = tx.getCurrentLocale() ?? '';
  const [status, setStatus] = useState<SseConnectionStatus>('disconnected');
  const [eventLog, setEventLog] = useState<EventLogEntry[]>([]);
  const [stats, setStats] = useState<GamificationStatsDto | null>(null);
  const clientRef = useRef<SseClient | null>(null);
  const connectIdRef = useRef(0);

  const ackQueuedRef = useRef(new Set<string>());
  const ackPendingRef = useRef(new Set<string>());
  const ackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushXpAcks = useCallback(() => {
    if (ackTimerRef.current != null) {
      clearTimeout(ackTimerRef.current);
      ackTimerRef.current = null;
    }
    const queued = ackQueuedRef.current;
    if (queued.size === 0) return;
    const ids = [...queued];
    queued.clear();
    const pending = ackPendingRef.current;
    for (const id of ids) {
      pending.add(id);
    }
    void postXpAcknowledgements(ids).finally(() => {
      for (const id of ids) {
        pending.delete(id);
      }
    });
  }, []);

  const enqueueXpAck = useCallback(
    (eventId: string) => {
      const queued = ackQueuedRef.current;
      const pending = ackPendingRef.current;
      if (pending.has(eventId) || queued.has(eventId)) return;
      queued.add(eventId);
      if (ackTimerRef.current == null) {
        ackTimerRef.current = setTimeout(() => {
          ackTimerRef.current = null;
          flushXpAcks();
        }, XP_ACK_DEBOUNCE_MS);
      }
    },
    [flushXpAcks]
  );

  const handleEvent = useCallback(
    (event: SseXpEvent) => {
      setEventLog(prev => [{ id: crypto.randomUUID(), event }, ...prev].slice(0, MAX_EVENT_LOG));

      if (event.type === 'unknown') {
        console.warn('[SSE] Unknown event received:', event.raw);
        return;
      }

      setStats(event.event.stats);
      showXpEventToast(event.event);
      enqueueXpAck(event.event.id);
    },
    [enqueueXpAck]
  );

  const disconnect = useCallback(() => {
    flushXpAcks();

    ++connectIdRef.current;
    clientRef.current?.close();
    clientRef.current = null;
    setStatus('disconnected');
  }, [flushXpAcks]);

  const connect = useCallback(() => {
    disconnect();

    const id = ++connectIdRef.current;
    const isActiveConnect = () => connectIdRef.current === id;
    setStatus('connecting');

    buildSseUrlWithToken()
      .then(url => {
        if (!isActiveConnect()) return;

        const client = createSseClient({
          url,
          onEvent: event => {
            if (!isActiveConnect()) return;
            handleEvent(event);
          },
          onOpen: () => {
            if (!isActiveConnect()) return;
            setStatus('connected');
          },
          onError: () => {
            if (!isActiveConnect()) return;
            setStatus('error');
          },
        });

        if (!isActiveConnect()) {
          client.close();
          return;
        }

        clientRef.current = client;
      })
      .catch(err => {
        if (!isActiveConnect()) return;
        SentryClient.captureException(err, {
          extra: { context: 'buildSseUrlWithToken', message: 'Failed to build SSE URL' },
        });
        setStatus('disconnected');
      });
  }, [disconnect, handleEvent]);

  // `locale` is in the dependency array so we reconnect when Transifex’s current locale changes
  // and new SSE payloads match the user’s language.
  useEffect(() => {
    if (!autoConnect) return;
    connect();
    return () => disconnect();
  }, [autoConnect, connect, disconnect, locale]);

  useEffect(() => () => flushXpAcks(), [flushXpAcks]);

  return { status, eventLog, stats, handleEvent, connect, disconnect };
}
